import { useGetShortQuestionnaireSummaryReportQuery } from '@report/graphql';
import { dayjs } from '@shared/utils';
import { useMemo } from 'react';

import {
  buildShortFormSections,
  type ShortFormSection,
} from '../../containers/questionnaire-report-short-form-template/utils';
import { useIsQuestionnaireReportFilterApplied } from '../use-is-questionnaire-report-filter-applied';
import { useQuestionnaireReportSearchParams } from '../use-questionnaire-report-search-params';

export function useShortFormTemplateData(): {
  sections: ShortFormSection[];
  loading: boolean;
} {
  const [{ examStartDate, examEndDate, examCenter, examCourse }] =
    useQuestionnaireReportSearchParams();
  const isFilterApplied = useIsQuestionnaireReportFilterApplied();

  const { data, loading } = useGetShortQuestionnaireSummaryReportQuery({
    variables: {
      input: {
        startedAt: (examStartDate ? dayjs(examStartDate).startOf('day').toDate() : null) as Date,
        endedAt: (examEndDate ? dayjs(examEndDate).endOf('day').toDate() : null) as Date,
        examCenterCodes: examCenter.includes('all') ? null : examCenter,
        examCourseCodes: examCourse.includes('all') ? null : examCourse,
      },
    },
    skip: !isFilterApplied,
  });

  // `report` is a plain (referentially stable) read off Apollo's `data`, so it
  // needs no memo of its own; the memo belongs on `sections`, which builds new
  // arrays. While the filter is applied the scaffold is always built (a
  // placeholder one during loading) so the table stays on screen for the
  // skeleton state. Before any filter is applied there is nothing to show.
  const report = data?.reportGetShortQuestionnaireSummaryReport;
  const sections = useMemo(
    () => (isFilterApplied ? buildShortFormSections(report) : []),
    [isFilterApplied, report],
  );

  return { sections, loading };
}
